/**
 * Minimal replacement for the `is-hotkey` package (default, keycode-style
 * matching). We only ever call the two-argument form `isHotkey(hotkey, event)`
 * with single keys and `mod`-prefixed combos, so this supports a "modifier+key"
 * grammar and matches modifiers exactly: any modifier not named in the hotkey
 * must NOT be pressed (same semantics as is-hotkey).
 *
 * Matching is done against `event.key` (case-insensitive) rather than the
 * deprecated `which`/keyCode.
 */

/** Guarded against `window` so importing this module stays safe outside the browser. */
export const IS_MAC =
  typeof window !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(window.navigator.platform);

type ModifierFlag = 'altKey' | 'ctrlKey' | 'metaKey' | 'shiftKey';

const MODIFIER_TOKENS: Record<string, ModifierFlag> = {
  alt: 'altKey',
  option: 'altKey',
  opt: 'altKey',
  ctrl: 'ctrlKey',
  control: 'ctrlKey',
  ctl: 'ctrlKey',
  meta: 'metaKey',
  cmd: 'metaKey',
  command: 'metaKey',
  win: 'metaKey',
  windows: 'metaKey',
  shift: 'shiftKey'
};

// Maps a hotkey token to the lower-cased `event.key` it should match.
const KEY_ALIASES: Record<string, string> = {
  left: 'arrowleft',
  right: 'arrowright',
  up: 'arrowup',
  down: 'arrowdown',
  space: ' ',
  spacebar: ' ',
  esc: 'escape',
  del: 'delete',
  return: 'enter'
};

interface ParsedHotkey {
  modifiers: Record<ModifierFlag, boolean>;
  key: string | null;
}

function parseHotkey(hotkey: string): ParsedHotkey {
  const modifiers: Record<ModifierFlag, boolean> = {
    altKey: false,
    ctrlKey: false,
    metaKey: false,
    shiftKey: false
  };
  let key: string | null = null;

  // `++` means a literal `+` key; preserve it before splitting on the separator.
  for (const raw of hotkey.replace('++', '+add').split('+')) {
    const token = raw.toLowerCase();

    if (token === 'mod') {
      modifiers[IS_MAC ? 'metaKey' : 'ctrlKey'] = true;
    } else if (MODIFIER_TOKENS[token]) {
      modifiers[MODIFIER_TOKENS[token]] = true;
    } else {
      key = KEY_ALIASES[token] ?? (token === 'add' ? '+' : token);
    }
  }

  return {modifiers, key};
}

type HotkeyEvent = Pick<KeyboardEvent, 'key' | 'altKey' | 'ctrlKey' | 'metaKey' | 'shiftKey'>;

export default function isHotkey(hotkey: string, event: HotkeyEvent): boolean {
  const {modifiers, key} = parseHotkey(hotkey);

  if (
    event.altKey !== modifiers.altKey ||
    event.ctrlKey !== modifiers.ctrlKey ||
    event.metaKey !== modifiers.metaKey ||
    event.shiftKey !== modifiers.shiftKey
  ) {
    return false;
  }

  if (key == null) {
    return true;
  }

  return event.key != null && event.key.toLowerCase() === key;
}
